'use client';

import { createComponent } from '@carvana/merch-components';
import dynamic from 'next/dynamic';
import { isNonProd, isProd } from '~/constants/environment';
import useErrorPage from '~/hooks/common/appRouter/useErrorPage';

const PageHeader = dynamic(
  () => import(/* webpackChunkName: "PageHeader" */ '@/common/PageHeader'),
  { ssr: false }
);

const ErrorSadCar = `${process.env.CVNA_APP_CDN_ASSETS_PATH}/images/error-sadcar-device.png`;

const ErrorBoundaryFallbackWrapper = createComponent('div', {
  className: 'min-h-[50vh] text-center'
});

const ErrorBoundaryFallbackTitle = createComponent('p', {
  className: 'mt-48 tl-header-xxl'
});

const ErrorBoundaryFallbackLogo = createComponent('img', {
  className: 'mx-auto my-48 h-[118px] w-[118px]'
});

const ErrorBoundaryFallbackLink = createComponent('a', {
  className: 'font-bold text-blue-2 no-underline'
});

type OwnProps = {
  error?: Error & { digest?: string };
  inCarvanaLayout?: boolean;
  page: string;
  statusCode?: number;
};

export default function CommonErrorPage({
  error,
  inCarvanaLayout = false,
  page,
  statusCode = 500
}: OwnProps) {
  const errorCopy =
    statusCode === 404
      ? 'We could not find that vehicle.'
      : 'It appears there may have been a problem.';

  useErrorPage(page, error);

  return (
    <>
      {!inCarvanaLayout && <PageHeader />}

      <ErrorBoundaryFallbackWrapper>
        <ErrorBoundaryFallbackTitle>
          Oops! {errorCopy}
        </ErrorBoundaryFallbackTitle>
        <p className="mt-8">
          It appears there may have been a problem with the {page} page.
        </p>
        <p>We are sorry for the inconvenience.</p>

        <div>
          <ErrorBoundaryFallbackLogo src={ErrorSadCar} />
        </div>

        {isProd ? (
          <div className="flex justify-center gap-4">
            <ErrorBoundaryFallbackLink
              href="/cars"
              data-analytics-id={`${page} Error Fallback`}
              data-testid="error-fallback-search-link"
            >
              Get back to SEARCH
            </ErrorBoundaryFallbackLink>
          </div>
        ) : (
          <div className="mt-8">
            <ErrorBoundaryFallbackLink
              href="/cars"
              data-analytics-id={`${page} Error Fallback`}
              data-testid="error-fallback-search-link"
            >
              Get back to SEARCH
            </ErrorBoundaryFallbackLink>
            {isNonProd && (
              <div className="mx-auto max-w-4xl whitespace-pre-wrap text-left text-sm">
                {error && error.toString()}
                <br />
                {error && error?.stack}
              </div>
            )}
          </div>
        )}
      </ErrorBoundaryFallbackWrapper>
    </>
  );
}
